// my ticket 

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePriorities } from '../../../hooks/usePriorities';
import { useCategories } from '../../../hooks/useCategories';
import { useStatuses } from '../../../hooks/useStatuses';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketService } from '../../../api/ticketService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { minutesToHHMMSS, formatTimeWithSeconds } from '../../../components/globaldeclaration';
import { Alert } from '../../../components/ui/Alert';
import {
  Ticket,
  Search,
  Filter,
  Clock,
  User,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  MoreHorizontal,
  Eye,
  Edit,
  Archive,
  Star,
  Flag,
  Calendar,
  Tag,
  RefreshCw,
  X,
  Paperclip,
  Pen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../../utils/exportStyledXlsx';

const MyTickets = ({ onCreateTicket }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading state for search operations
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [showFilters, setShowFilters] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10); // Default page size (not used when fetchAll=true)
  const [paginationData, setPaginationData] = useState(null);
  const { data: priorities = [], isSuccess: prioritiesReady } = usePriorities();
  const { data: categories = [] } = useCategories();
  const { data: statusesRaw = [] } = useStatuses();
  const statuses = useMemo(
    () => statusesRaw.filter((status) => status.isActive !== false),
    [statusesRaw]
  );
  // SLA timer state - Map of ticketId to { minutes, seconds }
  const [slaTimers, setSlaTimers] = useState(new Map());
  // Statistics state for accurate counts
  const [statistics, setStatistics] = useState({
    total: 0,
    open: 0,
    pending: 0,
    'in-progress': 0,
    resolved: 0,
    closed: 0,
    escalated: 0
  });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const loadTicketsRef = useRef(null);
  const loadStatisticsRef = useRef(null);
  const ticketsSlaFetchedAtRef = useRef(Date.now());
  const hasInitializedRef = useRef(false);
  const skipInitialFilterLoadRef = useRef(true);
  const skipInitialSearchLoadRef = useRef(true);

  const computeSlaTimerForTicket = (ticket, secondsSinceFetch) => {
    const apiOverdueMinutes = ticket.slaOverdueMinutes ?? 0;
    const apiOverdueSeconds = ticket.slaOverdueSeconds ?? 0;
    const isBreachedFromApi =
      ticket.slaIsBreached === true
      || ticket.slaIsEscalated === true
      || apiOverdueMinutes > 0
      || apiOverdueSeconds > 0;

    if (isBreachedFromApi) {
      const totalOverdueSeconds =
        apiOverdueMinutes * 60 + apiOverdueSeconds + secondsSinceFetch;
      return {
        minutes: Math.floor(totalOverdueSeconds / 60),
        seconds: totalOverdueSeconds % 60,
        isOverdue: true,
      };
    }

    if (
      ticket.slaRemainingMinutes === null
      || ticket.slaRemainingMinutes === undefined
    ) {
      return null;
    }

    const apiRemainingMinutes = ticket.slaRemainingMinutes ?? 0;
    const apiRemainingSeconds = ticket.slaRemainingSeconds ?? 0;
    // const totalRemainingSeconds = Math.max(
    //   0,
    //   apiRemainingMinutes * 60 + apiRemainingSeconds - secondsSinceFetch
    // );
    const totalRemainingSeconds =
      apiRemainingMinutes * 60 + apiRemainingSeconds - secondsSinceFetch;

    // return {
    //   minutes: Math.floor(totalRemainingSeconds / 60),
    //   seconds: totalRemainingSeconds % 60,
    //   isOverdue: false,
    // };

    if (totalRemainingSeconds > 0) {
      return {
        minutes: Math.floor(totalRemainingSeconds / 60),
        seconds: totalRemainingSeconds % 60,
        isOverdue: false,
      };
    }

    // AUTO TRANSITION TO OVERDUE
    const overdueSeconds = Math.abs(totalRemainingSeconds);

    return {
      minutes: Math.floor(overdueSeconds / 60),
      seconds: overdueSeconds % 60,
      isOverdue: true,
    };
  };

  useEffect(() => {
    if (!prioritiesReady || hasInitializedRef.current) {
      return;
    }
    const initializeData = async () => {
      await loadTickets(false, priorities);
      hasInitializedRef.current = true;
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritiesReady, priorities]);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const response = await ticketService.getMyTicketsStatistics();
        if (cancelled) return;
        if (response?.data?.success && response?.data?.data) {
          const stats = response.data.data;
          setStatistics({
            total: stats.total || 0,
            open: stats.open || 0,
            pending: stats.pending || 0,
            'in-progress': stats['in-progress'] || 0,
            resolved: stats.resolved || 0,
            closed: stats.closed || 0,
            escalated: stats.escalated || 0
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load statistics:', error);
        }
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load statistics for accurate counts (once on mount; refresh via handleRefresh / realtime)
  const loadStatistics = async () => {
    try {
      const response = await ticketService.getMyTicketsStatistics();
      if (response?.data?.success && response?.data?.data) {
        const stats = response.data.data;
        setStatistics({
          total: stats.total || 0,
          open: stats.open || 0,
          pending: stats.pending || 0,
          'in-progress': stats['in-progress'] || 0,
          resolved: stats.resolved || 0,
          closed: stats.closed || 0,
          escalated: stats.escalated || 0
        });
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      // Keep default values (0) on error
    }
  };

  // Reload tickets when page changes (server-side pagination)
  // Skip this if searchTerm is set (search effect will handle it)
  useEffect(() => {
    if (!hasInitializedRef.current) return;
    if (priorities.length > 0 && (!searchTerm || !searchTerm.trim())) {
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Server-side search: when searchTerm changes, fetch tickets with search parameter (soft patch - only update tickets, not statistics)
  useEffect(() => {
    if (!hasInitializedRef.current || priorities.length === 0) return undefined;

    // Initial mount uses the bootstrap effect; skip empty search until the user types
    if (skipInitialSearchLoadRef.current) {
      skipInitialSearchLoadRef.current = false;
      if (!searchTerm?.trim()) {
        return undefined;
      }
    }

    if (currentPage !== 0) {
      setCurrentPage(0);
      return undefined;
    }

    const searchTimeout = setTimeout(() => {
      loadTickets(true);
    }, searchTerm.trim() ? 500 : 300);

    return () => clearTimeout(searchTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (tickets.length > 0) {
      filterTickets();
    }
  }, [sortBy, tickets]);

  // Reload tickets when status, priority, or category filters change (not on initial mount)
  useEffect(() => {
    if (!hasInitializedRef.current || priorities.length === 0) return;

    if (skipInitialFilterLoadRef.current) {
      skipInitialFilterLoadRef.current = false;
      return;
    }

    if (currentPage !== 0) {
      setCurrentPage(0);
      return;
    }

    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, categoryFilter]);

  // SLA timers: seed from API remaining/overdue fields, tick every second
  useEffect(() => {
    if (tickets.length === 0) return;

    const buildTimers = (secondsSinceFetch) => {
      const nextTimers = new Map();
      tickets.forEach((ticket) => {
        const ticketStatus = ticket.status?.toLowerCase();
        if (ticketStatus === 'closed' || !ticket.assignedTime) {
          return;
        }
        const timer = computeSlaTimerForTicket(ticket, secondsSinceFetch);
        if (timer) {
          nextTimers.set(ticket.id, timer);
        }
      });
      return nextTimers;
    };

    setSlaTimers(buildTimers(0));

    const interval = setInterval(() => {
      const tickSeconds = Math.floor(
        (Date.now() - ticketsSlaFetchedAtRef.current) / 1000
      );
      setSlaTimers(buildTimers(tickSeconds));
    }, 1000);

    return () => clearInterval(interval);
  }, [tickets]);


  const loadTickets = async (isSearchOperation = false, prioritiesOverride = null) => {
    try {
      // Use searchLoading for search operations to avoid full page reload
      if (isSearchOperation) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      let availablePriorities = prioritiesOverride ?? priorities;

      // Fetch tickets with server-side pagination and filters
      const params = { page: currentPage, size: pageSize };

      // Add search filter if provided
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add status filter if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add priority filter if not 'all'
      if (priorityFilter && priorityFilter !== 'all') {
        // Find priorityId from priority code/name
        const selectedPriority = availablePriorities.find(pri =>
          (pri.priorityCode || '').toLowerCase() === priorityFilter.toLowerCase() ||
          (pri.priorityName || '').toLowerCase() === priorityFilter.toLowerCase() ||
          pri.priorityId?.toString() === priorityFilter
        );
        if (selectedPriority && selectedPriority.priorityId) {
          params.priorityId = selectedPriority.priorityId;
        }
      }

      // Add category filter if not 'all'
      if (categoryFilter && categoryFilter !== 'all') {
        // Find categoryId from category name
        const selectedCategory = categories.find(cat =>
          (cat.categoryName || '').toLowerCase() === categoryFilter.toLowerCase() ||
          cat.categoryId?.toString() === categoryFilter
        );
        if (selectedCategory && selectedCategory.categoryId) {
          params.categoryId = selectedCategory.categoryId;
        }
      }

      const response = await ticketService.getMyTickets(params);

      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // data is PaginatedResponse with content, pageNumber, pageSize, totalElements, etc.
      const responseData = response?.data;

      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const backendTickets = paginatedData.content || [];

        // Transform backend tickets to frontend format
        const transformedTickets = backendTickets.map(ticket => {
          // Map priorityId to priority name/code
          let priority = 'medium';
          let priorityName = 'Medium';
          let priorityId = ticket.priorityId;

          if (priorityId && availablePriorities.length > 0) {
            const priorityObj = availablePriorities.find(p => p.priorityId === priorityId || p.priorityId?.toString() === priorityId?.toString());
            if (priorityObj) {
              priority = (priorityObj.priorityCode || priorityObj.priorityName || 'medium').toLowerCase();
              priorityName = priorityObj.priorityName || priorityObj.priorityCode || 'Medium';
            }
          } else if (ticket.priority) {
            // Fallback to ticket.priority if available
            priority = ticket.priority.toLowerCase();
            priorityName = ticket.priority;
          }

          // Determine SLA status from backend data
          let slaStatus = 'on-track';
          if (ticket.slaIsEscalated) {
            slaStatus = 'breached';
          } else if (ticket.slaIsBreached) {
            slaStatus = 'breached';
          } else if (ticket.slaRemainingMinutes !== null && ticket.slaRemainingMinutes !== undefined) {
            const remainingTotalSeconds =
              (ticket.slaRemainingMinutes ?? 0) * 60 + (ticket.slaRemainingSeconds ?? 0);
            if (remainingTotalSeconds < 3600) {
              slaStatus = 'at-risk';
            } else {
              slaStatus = 'on-track';
            }
          }

          return {
            id: ticket.ticketId || ticket.ticketNumber,
            ticketId: ticket.ticketId, // Store numeric ticketId separately for navigation
            ticketNumber: ticket.ticketNumber,
            title: ticket.subject,
            description: ticket.description || '',
            status: ticket.status?.toLowerCase() || 'open',
            priority: priority,
            priorityName: priorityName,
            priorityId: priorityId,
            category: ticket.category || 'Uncategorized',
            categoryId: ticket.categoryId, // Store categoryId for updates
            customer: {
              name: ticket.requesterEmail ? ticket.requesterEmail.split('@')[0] : 'Unknown',
              email: ticket.requesterEmail || '',
              company: '' // Not available in backend response
            },
            assignedAgent: ticket.assigneeId ? `Agent ${ticket.assigneeId}` : 'Unassigned',
            assignedTime: ticket.assignedTime,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt || ticket.createdAt,
            dueDate: ticket.resolvedAt || ticket.updatedAt,
            tags: [], // Not available in backend response
            attachments: ticket.attachmentCount || 0,
            comments: ticket.replyCount || 0,
            slaStatus: slaStatus,
            slaRemainingMinutes: ticket.slaRemainingMinutes,
            slaRemainingSeconds: ticket.slaRemainingSeconds,
            slaOverdueMinutes: ticket.slaOverdueMinutes,
            slaOverdueSeconds: ticket.slaOverdueSeconds,
            slaElapsedMinutes: ticket.slaElapsedMinutes,
            slaIsBreached: ticket.slaIsBreached,
            slaIsEscalated: ticket.slaIsEscalated,
            timeSpent: '0h 0m', // Not available in backend response
            estimatedTime: '0h', // Not available in backend response
            lastActivity: ticket.updatedAt || ticket.createdAt,
            // Location fields
            countryId: ticket.countryId,
            regionId: ticket.regionId,
            countyId: ticket.countyId,
            subCountyId: ticket.subCountyId,
            wardId: ticket.wardId,
            countryName: ticket.countryName,
            regionName: ticket.regionName,
            countyName: ticket.countyName,
            subCountyName: ticket.subCountyName,
            wardName: ticket.wardName
          };
        });

        ticketsSlaFetchedAtRef.current = Date.now();
        setTickets(transformedTickets);
        // For server-side pagination, filteredTickets should only contain current page tickets
        // Client-side filtering will be applied on the current page's tickets
        setFilteredTickets(transformedTickets);

        // Store pagination data
        setPaginationData({
          pageNumber: paginatedData.pageNumber || currentPage,
          pageSize: paginatedData.pageSize || pageSize,
          totalElements: paginatedData.totalElements || 0,
          totalPages: paginatedData.totalPages || 0,
          first: paginatedData.first !== undefined ? paginatedData.first : (currentPage === 0),
          last: paginatedData.last !== undefined ? paginatedData.last : false
        });

      } else {
        setError(responseData?.message || 'Failed to load tickets');
        toast.error(responseData?.message || 'Failed to load tickets');
        // Don't clear tickets during search - keep existing data visible
        if (!isSearchOperation) {
          setTickets([]);
          setFilteredTickets([]);
        }
      }
    } catch (err) {
      console.error('Error loading tickets:', err);
      const errorMsg = extractErrorMessage(err, 'Error loading tickets');
      setError(errorMsg);
      toast.error(errorMsg);
      // Don't clear tickets during search - keep existing data visible
      if (!isSearchOperation) {
        setTickets([]);
        setFilteredTickets([]);
      }
    } finally {
      // Reset appropriate loading state
      if (isSearchOperation) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  loadTicketsRef.current = loadTickets;
  loadStatisticsRef.current = loadStatistics;

  useEffect(() => {
    const onRealtimeRefresh = () => {
      loadStatisticsRef.current?.();
      loadTicketsRef.current?.(true);
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, []);

  const filterTickets = () => {
    let filtered = [...tickets];

    // Search, status, priority, and category filters are handled server-side
    // Only sorting is done client-side

    // Sort tickets
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'updated':
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        case 'priority':
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'due':
          return new Date(a.dueDate) - new Date(b.dueDate);
        default:
          return 0;
      }
    });

    setFilteredTickets(filtered);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'in-progress':
        return <Edit className="w-4 h-4 text-blue-500" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'escalated':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Ticket className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      'in-progress': 'default',
      resolved: 'success',
      escalated: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status.replace('-', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority, priorityName = null) => {
    const variants = {
      critical: 'destructive',
      high: 'destructive',
      medium: 'warning',
      low: 'success'
    };
    const displayText = priorityName || priority;
    return <Badge variant={variants[priority] || 'secondary'}>{displayText}</Badge>;
  };

  const getSLAStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track':
        return 'text-green-600';
      case 'at-risk':
        return 'text-yellow-600';
      case 'breached':
        return 'text-red-600';
      case 'completed':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent': return 'text-white bg-red-800 dark:bg-red-900';
      case 'critical': return 'text-red-800 bg-red-100 dark:text-red-200 dark:bg-red-900/50';
      case 'high': return 'text-orange-700 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/40';
      case 'medium': return 'text-yellow-800 bg-yellow-100 dark:text-yellow-200 dark:bg-yellow-900/40';
      case 'low': return 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/40';
      default: return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700';
    }
  };

  // Get light background color for ticket row based on priority
  const getPriorityBackgroundColor = (priority) => {
    // Return default background - no priority-based colors
    return 'bg-white dark:bg-gray-800';
  };

  // Get darker border color for ticket row based on priority
  const getPriorityBorderColor = (priority) => {
    return '';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()?.replace(/[_-]/g, ' ')) {
      case 'new':
      case 'open': return 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/40';
      case 'pending':
      case 'pending customer': return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700';
      case 'in progress':
      case 'assigned': return 'text-teal-800 bg-teal-100 dark:text-teal-200 dark:bg-teal-900/40';
      case 'resolved':
      case 'closed': return 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/40';
      case 'on hold': return 'text-yellow-700 bg-yellow-100 dark:text-yellow-300 dark:bg-yellow-900/40';
      case 'cancelled':
      case 'canceled': return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700';
      case 'reopen':
      case 're opened':
      case 're open': return 'text-purple-700 bg-purple-100 dark:text-purple-300 dark:bg-purple-900/40';
      case 'escalated': return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/40';
      default: return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700';
    }
  };

  const formatStatusLabel = (status) => {
    if (!status) return 'N/A';
    return String(status).replace(/[_-]/g, ' ').toUpperCase();
  };

  const getTimeRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;

    if (diff < 0) return 'Overdue';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Format time from minutes to readable string
  const formatTimeFromMinutes = (minutes) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    const absMinutes = Math.abs(minutes);
    if (absMinutes < 60) {
      return minutes < 0 ? `${absMinutes}m overdue` : `${absMinutes}m`;
    } else if (absMinutes < 1440) {
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return minutes < 0 ? `${hours}h ${mins}m overdue` : `${hours}h ${mins}m`;
    } else {
      const days = Math.floor(absMinutes / 1440);
      const hours = Math.floor((absMinutes % 1440) / 60);
      return minutes < 0 ? `${days}d ${hours}h overdue` : `${days}d ${hours}h`;
    }
  };

  const handleViewTicket = (ticketIdOrNumber) => {
    // Ensure we use numeric ticketId for navigation
    // ticketIdOrNumber could be numeric ticketId or ticket number string
    let numericTicketId = ticketIdOrNumber;

    // If it's a string that looks like a ticket number (e.g., "TKT-002997"), try to find the numeric ID
    if (typeof ticketIdOrNumber === 'string' && ticketIdOrNumber.includes('TKT-')) {
      const ticket = tickets.find(t => t.ticketNumber === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    } else {
      // Try to find ticket to get numeric ID if we have a numeric value
      const ticket = tickets.find(t => t.ticketId === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    }

    // Validate that we have a valid ID (numeric or valid string)
    if (!numericTicketId || (typeof numericTicketId === 'string' && numericTicketId.includes('TKT-'))) {
      toast.error('Invalid ticket ID. Please refresh and try again.');
      return;
    }

    const ticket = tickets.find(t => t.ticketId === numericTicketId || t.id === numericTicketId);
    toast.success(`Opening ticket ${ticket?.ticketNumber || numericTicketId} for editing...`);
    navigate(`/agent/tickets/${numericTicketId}`, { state: { mode: 'edit' } });
  };


  const handleCommentTicket = (ticketIdOrNumber) => {
    // Ensure we use numeric ticketId for navigation
    // ticketIdOrNumber could be numeric ticketId or ticket number string
    let numericTicketId = ticketIdOrNumber;

    // If it's a string that looks like a ticket number (e.g., "TKT-002997"), try to find the numeric ID
    if (typeof ticketIdOrNumber === 'string' && ticketIdOrNumber.includes('TKT-')) {
      const ticket = tickets.find(t => t.ticketNumber === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    } else {
      // Try to find ticket to get numeric ID if we have a numeric value
      const ticket = tickets.find(t => t.ticketId === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    }

    // Validate that we have a valid ID (numeric or valid string)
    if (!numericTicketId || (typeof numericTicketId === 'string' && numericTicketId.includes('TKT-'))) {
      toast.error('Invalid ticket ID. Please refresh and try again.');
      return;
    }

    const ticket = tickets.find(t => t.ticketId === numericTicketId || t.id === numericTicketId);
    toast.success(`Opening ticket ${ticket?.ticketNumber || numericTicketId} in view mode...`);
    navigate(`/agent/tickets/${numericTicketId}`, { state: { mode: 'view', openComments: true, scrollToComments: true } });
  };

  const handleEscalateTicket = (ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    const ticketStatus = ticket?.status?.toLowerCase();

    // Prevent escalation for closed or resolved tickets
    if (ticketStatus === 'closed' || ticketStatus === 'resolved') {
      toast.error(`Cannot escalate ticket ${ticketId}. Ticket is ${ticketStatus}.`);
      return;
    }

    if (ticketStatus === 'escalated') {
      toast.error(`Ticket ${ticketId} is already escalated`);
      return;
    }
    toast.warning(`Escalating ticket ${ticketId}...`);
    navigate('/agent/tickets/escalation', { state: { ticketId, ticket } });
  };

  const handleResolveTicket = (ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket?.status === 'resolved') {
      toast(`Ticket ${ticketId} is already resolved`, { icon: 'ℹ️' });
      return;
    }
    toast.success(`Opening resolution form for ${ticketId}...`);
    navigate('/agent/tickets/resolution', { state: { ticketId, ticket } });
  };

  const handleMoreAction = (action, ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);

    switch (action) {
      case 'archive':
        setTickets(prev => prev.map(t =>
          t.id === ticketId ? { ...t, archived: true, status: 'archived' } : t
        ));
        toast.success(`Ticket ${ticketId} archived successfully`);
        break;
      case 'star':
        setTickets(prev => prev.map(t =>
          t.id === ticketId ? { ...t, starred: !t.starred } : t
        ));
        const isStarred = ticket?.starred;
        toast.success(`Ticket ${ticketId} ${isStarred ? 'unstarred' : 'starred'}`);
        break;
      case 'assign':
        toast(`Opening reassignment dialog for ${ticketId}...`, { icon: '📋' });
        navigate('/agent/tickets/assignment', { state: { ticketId, ticket, isReassignment: true } });
        break;
      default:
        toast(`Action: ${action}`, { icon: 'ℹ️' });
    }
    setShowMoreMenu(null);
  };

  const handleRefresh = () => {
    loadStatistics();
    loadTickets();
  };

  const handleNewTicket = () => {
    toast.success('Opening new ticket form...');
    if (onCreateTicket) {
      onCreateTicket();
      return;
    }
    navigate('/agent/tickets/new');
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
    toast(showFilters ? 'Filters hidden' : 'Filters shown', { icon: 'ℹ️' });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
    toast.success('All filters cleared');
  };

  const formatDateTimeExport = (isoString) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '-';
    }
  };

  const fetchMyTicketsForExport = async () => {
    const params = { fetchAll: true };
    if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (priorityFilter && priorityFilter !== 'all') {
      const selectedPriority = priorities.find(pri =>
        (pri.priorityCode || '').toLowerCase() === priorityFilter.toLowerCase() ||
        (pri.priorityName || '').toLowerCase() === priorityFilter.toLowerCase() ||
        pri.priorityId?.toString() === priorityFilter
      );
      if (selectedPriority?.priorityId) params.priorityId = selectedPriority.priorityId;
    }
    if (categoryFilter && categoryFilter !== 'all') {
      const selectedCategory = categories.find(cat =>
        (cat.categoryName || '').toLowerCase() === categoryFilter.toLowerCase() ||
        cat.categoryId?.toString() === categoryFilter
      );
      if (selectedCategory?.categoryId) params.categoryId = selectedCategory.categoryId;
    }
    const response = await ticketService.getMyTickets(params);
    const data = response?.data?.data;
    const content = Array.isArray(data) ? data : (data?.content || []);
    const availablePriorities = priorities;
    return (content || []).map((ticket) => {
      let priorityName = 'Medium';
      const priorityId = ticket.priorityId;
      if (priorityId && availablePriorities.length > 0) {
        const p = availablePriorities.find(pr => pr.priorityId === priorityId || String(pr.priorityId) === String(priorityId));
        if (p) priorityName = p.priorityName || p.priorityCode || 'Medium';
      } else if (ticket.priority) priorityName = ticket.priority;
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || '',
        category: ticket.category || 'Uncategorized',
        status: (ticket.status || 'open').toLowerCase(),
        priorityName,
        createdAt: ticket.createdAt
      };
    });
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchMyTicketsForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const headers = ['S.NO', 'Ticket Number', 'Subject', 'Category', 'Priority', 'Status', 'Created'];
      const excelRows = rows.map((r, i) => [
        i + 1, r.ticketNumber, r.subject, r.category, r.priorityName, r.status, r.createdAt || '',
      ]);
      downloadStyledXlsx({
        fileName: `my-tickets-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'My Tickets',
        headers,
        rows: excelRows,
        numberColumns: [0],
        textColumns: [1, 2, 3, 4, 5],
        dateColumns: [6],
        centerColumns: [0, 4, 5, 6],
      });
      toast.success('Tickets exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchMyTicketsForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('My Tickets', 14, 15);
      doc.setFontSize(10);
      const body = rows.map((r, i) => [
        i + 1, r.ticketNumber, (r.subject || '').substring(0, 35), (r.category || '').substring(0, 15), r.priorityName, r.status, formatDateTimeExport(r.createdAt)
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'Ticket Number', 'Subject', 'Category', 'Priority', 'Status', 'Created']],
        body,
        styles: { fontSize: 7 }
      });
      doc.save(`my-tickets-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Tickets</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your assigned tickets and track your progress
          </p>
        </div>
        <div className="flex gap-3 flex-nowrap">
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="relative flex-shrink-0">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setExportMenuOpen((v) => !v);
              }}
              disabled={loading || exporting}
            >
              <Download className="h-4 w-4" />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportExcel}
                  >
                    <FileText className="h-4 w-4" />
                    Export Excel
                  </button>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportPdf}
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleToggleFilters}
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
          <Button
            className="flex items-center gap-2"
            onClick={handleNewTicket}
          >
            <Ticket className="w-4 h-4" />
            New Ticket
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <div>
            <h4 className="font-semibold">Error loading tickets</h4>
            <p className="text-sm">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={loadTickets}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading tickets...</p>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <button
          onClick={() => {
            setStatusFilter('all');
            setPriorityFilter('all');
            toast.success('Showing all tickets');
          }}
          className="text-left hover:shadow-lg transition-shadow"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Ticket className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tickets</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {statistics.total}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>

        <button
          onClick={() => {
            setStatusFilter('open');
            setPriorityFilter('all');
            toast.success('Filtering by: Open tickets');
          }}
          className="text-left hover:shadow-lg transition-shadow"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Ticket className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {statistics.open}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>

        <button
          onClick={() => {
            setStatusFilter('in-progress');
            setPriorityFilter('all');
            toast.success('Filtering by: In Progress');
          }}
          className="text-left hover:shadow-lg transition-shadow"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {statistics['in-progress']}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>

        <button
          onClick={() => {
            setStatusFilter('escalated');
            setPriorityFilter('all');
            toast.success('Filtering by: Escalated tickets');
          }}
          className="text-left hover:shadow-lg transition-shadow"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Escalated</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {statistics.escalated}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>

        <button
          onClick={() => {
            setStatusFilter('resolved');
            setPriorityFilter('all');
            toast.success('Filtering by: Resolved tickets');
          }}
          className="text-left hover:shadow-lg transition-shadow"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {statistics.resolved}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>

        <button
          onClick={() => {
            setStatusFilter('closed');
            setPriorityFilter('all');
            toast.success('Filtering by: Closed tickets');
          }}
          className="text-left hover:shadow-lg transition-shadow"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <Archive className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Closed</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {statistics.closed}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-nowrap">
              {/* Search Input */}
              <div className="relative flex-1 min-w-0">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => {
                    e.preventDefault();
                    setSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    // Prevent form submission on Enter key
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full"
                />
              </div>

              {/* Status Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Status</option>
                  {statuses.map(status => {
                    const statusCode = status.statusCode || status.statusName?.toLowerCase() || '';
                    return (
                      <option key={status.statusId || statusCode} value={statusCode}>
                        {status.statusName || status.statusCode}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Priority Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Priority</option>
                  {priorities
                    .filter((priority) => priority.priorityId != null)
                    .map((priority) => {
                      const priorityCode = (priority.priorityCode || priority.priorityName || '').toLowerCase();
                      return (
                        <option key={priority.priorityId || priorityCode} value={priorityCode}>
                          {priority.priorityName || priority.priorityCode || priorityCode}
                        </option>
                      );
                    })}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Category Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="all">All Categories</option>
                  {categories.filter(cat => cat.isActive !== false && cat.categoryId !== null).map(category => (
                    <option key={category.categoryId} value={category.categoryId}>
                      {category.categoryName || 'Uncategorized'}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Sort By Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="updated">Last Updated</option>
                  <option value="created">Created Date</option>
                  <option value="priority">Priority</option>
                  <option value="due">Due Date</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Apply
                </Button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tickets List */}
      {!loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
          {/* Search Loading Indicator - Small progress bar at top of table */}
          {searchLoading && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 z-10">
              <div className="h-full bg-blue-600 animate-pulse" style={{ width: '30%' }}></div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    SLA Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTickets.map((ticket, index) => {
                  const priority = ticket.priority || 'medium';

                  // Check if ticket should be highlighted in light red
                  // Condition: status is "open" or "in-progress" AND SLA is timed out
                  const isOpenOrInProgress = ticket.status === 'open' || ticket.status === 'in-progress';
                  const timer = slaTimers.get(ticket.id);
                  const isSlaTimedOut = ticket.slaStatus === 'breached' ||
                    ticket.slaIsBreached ||
                    ticket.slaIsEscalated ||
                    (timer !== undefined && timer.minutes <= 0 && timer.seconds <= 0) ||
                    (timer === undefined && ticket.slaRemainingMinutes !== null && ticket.slaRemainingMinutes <= 0);
                  const shouldHighlight = isOpenOrInProgress && isSlaTimedOut;

                  const isReopened = ticket.status?.toLowerCase() === 'reopen';
                  const ticketId = ticket.ticketId || ticket.id;
                  return (
                    <tr
                      key={ticket.id}
                      className={
                        isReopened
                          ? 'bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100/60 dark:hover:bg-purple-900/30'
                          : shouldHighlight
                            ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100/70 dark:hover:bg-red-900/30 border-l-4 border-l-red-800'
                            : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                      }
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {currentPage * pageSize + index + 1}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleCommentTicket(ticketId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleCommentTicket(ticketId);
                            }
                          }}
                          className="flex items-center min-w-0 w-full text-left cursor-pointer hover:opacity-80 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          title="View ticket"
                        >
                          <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <Ticket className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {ticket.ticketNumber || ticket.id}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {ticket.title || ticket.subject}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-md ${getPriorityColor(ticket.priority || 'medium')}`}>
                          {(ticket.priorityName || ticket.priority || 'medium').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-md ${getStatusColor(ticket.status)}`}>
                            {formatStatusLabel(ticket.status)}
                          </span>
                          {isReopened && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md text-purple-700 dark:text-purple-300 bg-purple-200 dark:bg-purple-800 border border-purple-300 dark:border-purple-600">
                              REOPENED
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          {getStatusIcon(ticket.slaStatus || 'on-track')}
                          <div className={`text-sm ml-2 ${getSLAStatusColor(
                            slaTimers.get(ticket.id)?.isOverdue
                              ? 'breached'
                              : (ticket.slaStatus || 'on-track')
                          )
                            }`}>

                            <div>
                              {(() => {
                                const timer = slaTimers.get(ticket.id);

                                if (timer?.isOverdue) {
                                  return 'breached';
                                }

                                return ticket.slaStatus
                                  ? ticket.slaStatus.replace('-', ' ')
                                  : 'On Track';
                              })()}
                            </div>

                            <div>
                              {(() => {
                                // Use countdown timer if available, otherwise fallback to static value
                                const timer = slaTimers.get(ticket.id);
                                if (timer !== undefined) {
                                  if (timer.isOverdue) {
                                    return formatTimeWithSeconds(-timer.minutes, -timer.seconds);
                                  }
                                  return formatTimeWithSeconds(timer.minutes, timer.seconds);
                                }
                                if (
                                  ticket.slaIsBreached
                                  || ticket.slaIsEscalated
                                  || (ticket.slaOverdueMinutes ?? 0) > 0
                                  || (ticket.slaOverdueSeconds ?? 0) > 0
                                ) {
                                  const om = ticket.slaOverdueMinutes ?? 0;
                                  const os = ticket.slaOverdueSeconds ?? 0;
                                  return formatTimeWithSeconds(-om, -os);
                                }
                                if (
                                  ticket.slaRemainingMinutes !== null
                                  && ticket.slaRemainingMinutes !== undefined
                                ) {
                                  return formatTimeWithSeconds(
                                    ticket.slaRemainingMinutes,
                                    ticket.slaRemainingSeconds ?? 0
                                  );
                                }
                                return 'SLA Not Assigned';
                              })()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(ticket.createdAt)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatTime(ticket.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-1.5"
                            onClick={() => handleViewTicket(ticket.ticketId || ticket.id)}
                          >
                            <Pen className="w-4 h-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-1.5"
                            onClick={() => handleCommentTicket(ticket.ticketId || ticket.id)}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {paginationData && paginationData.totalPages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min((currentPage + 1) * pageSize, paginationData.totalElements)}</span> of{' '}
                    <span className="font-medium">{paginationData.totalElements}</span> results
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                    disabled={currentPage === 0 || paginationData.first}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                    let pageNum;
                    if (paginationData.totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage < 3) {
                      pageNum = i;
                    } else if (currentPage > paginationData.totalPages - 4) {
                      pageNum = paginationData.totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-md ${pageNum === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalPages - 1))}
                    disabled={currentPage >= paginationData.totalPages - 1 || paginationData.last}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredTickets.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Ticket className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No tickets found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your search criteria'
                : 'You have no assigned tickets at the moment'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && priorityFilter === 'all' && (
              <Button onClick={handleNewTicket}>
                <Ticket className="w-4 h-4 mr-2" />
                Request Assignment
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyTickets;
